class ReviewsCarouselLive extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector("swiper-container");
    this.loadReviews();
  }

  async loadReviews() {
    const store = this.dataset.store || "www.phytoceutics.com";
    const perPage = this.dataset.perPage || "12";
    const skus = this.dataset.skus || "";
    const handle = this.dataset.handle || "";

    try {
      const reviews = skus
        ? await this.fetchProductReviews(store, skus, perPage)
        : await this.fetchAllReviews(store, perPage);

      if (!reviews.length) {
        this.style.display = "none";
        return;
      }

      const handles = handle
        ? [handle]
        : [...new Set(reviews.map(r => this.extractHandle(r.productLink)).filter(Boolean))];

      const productMap = await this.fetchProductData(handles);

      reviews.forEach(r => {
        const productHandle = handle || this.extractHandle(r.productLink);
        this.carousel.appendChild(this.buildSlide(r, productMap[productHandle] || null));
      });

      this.initSwiper();
    } catch (e) {
      console.error("[reviews-carousel-live] fetch failed:", e);
      this.style.display = "none";
    }
  }

  async fetchAllReviews(store, perPage) {
    const url = `https://api.reviews.io/timeline/data?store=${store}&type=product_review&per_page=${perPage}&page=1&lang=en&sort=date_desc&must_have_comments=true`;
    const res = await fetch(url, { headers: { "Reviews-Origin": "REVIEWS.io Widget" } });
    const data = await res.json();
    return (data?.timeline || [])
      .map(item => {
        const r = item._source || item;
        return {
          rating: r.rating,
          comments: r.comments,
          author: r.author || "",
          verified: r.verified_by_shop,
          sku: r.sku || "",
          productLink: r.product_link || ""
        };
      })
      .filter(r => r.comments);
  }

  async fetchProductReviews(store, skus, perPage) {
    const url = `https://api.reviews.io/product/review?store=${store}&sku=${skus}&per_page=${perPage}&sort=date_desc`;
    const res = await fetch(url, { headers: { "Reviews-Origin": "REVIEWS.io Widget" } });
    const data = await res.json();
    return (data?.reviews?.data || [])
      .map(item => ({
        rating: item.rating,
        comments: item.review,
        author: `${item.reviewer?.first_name || ""} ${item.reviewer?.last_name || ""}`.trim(),
        verified: item.reviewer?.verified_buyer,
        sku: item.product?.sku || item.sku || "",
        productLink: ""
      }))
      .filter(r => r.comments);
  }

  extractHandle(productLink) {
    if (!productLink) return null;
    const match = productLink.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  async fetchProductData(handles) {
    const productMap = {};
    await Promise.all(handles.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return;
        const p = await res.json();
        productMap[handle] = {
          title: p.title,
          url: p.url || `/products/${handle}`,
          image: p.featured_image
            ? p.featured_image.replace(/(\.[^.?]+)(\?|$)/, "_120x120_crop_center$1$2")
            : null,
          variants: (p.variants || []).map(v => ({ sku: v.sku, title: v.title }))
        };
      } catch {}
    }));
    return productMap;
  }

  buildSlide(r, product) {
    const title = product?.title || "";
    const url = product?.url || r.productLink;
    const image = product?.image || null;
    const variant = product?.variants?.find(v => v.sku === r.sku);
    const variantTitle = variant?.title && variant.title !== "Default Title" ? variant.title : null;

    const slide = document.createElement("swiper-slide");
    slide.innerHTML = `
      <div class="review-card">
        <div class="star-rating">
          <span class="star-rating__rating" style="--rating:${r.rating * 20}%">★★★★★</span>
        </div>
        <blockquote class="review-card__content">
          <p class="p1">${r.comments}</p>
          <cite class="review-card__author p2">${r.author}</cite>
        </blockquote>
        ${r.verified ? `
          <div class="verified-buyer review-card__verified-buyer">
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" fill="none" viewBox="0 0 17 16">
              <g clip-path="url(#rcl-a)">
                <path fill="#95A681" d="M16.723 8c0 .864-1.05 1.517-1.306 2.286-.256.793.187 1.96-.28 2.613-.49.676-1.726.606-2.402 1.073-.653.49-.98 1.702-1.773 1.959-.77.256-1.726-.537-2.589-.537s-1.819.793-2.589.537c-.793-.257-1.12-1.47-1.772-1.96-.677-.49-1.913-.42-2.403-1.072-.49-.653-.023-1.82-.28-2.613C1.074 9.516.024 8.84.024 8.001c0-.84 1.05-1.516 1.307-2.286.256-.793-.187-1.96.28-2.612.49-.677 1.725-.607 2.402-1.073.653-.49.98-1.703 1.772-1.96.77-.256 1.726.537 2.59.537.862 0 1.819-.793 2.588-.536.793.256 1.12 1.469 1.773 1.959.676.49 1.912.42 2.402 1.073.49.653.024 1.819.28 2.612.257.77 1.306 1.446 1.306 2.286Z"/>
                <path stroke="#fff" stroke-linecap="round" stroke-miterlimit="10" stroke-width=".91" d="m5.271 8 2.17 2.053 3.988-4.222"/>
              </g>
              <defs><clipPath id="rcl-a"><path fill="#fff" d="M0 0h16.723v16H0z"/></clipPath></defs>
            </svg>
            <span>Verified Buyer</span>
          </div>
        ` : ""}
        ${title ? `
          <a href="${url}" class="review-card__product">
            ${image ? `
              <div class="review-card__product-media">
                <img src="${image}" alt="${title}" loading="lazy" width="56" height="56" />
              </div>
            ` : ""}
            <div class="review-card__product-content">
              <h3>${title}</h3>
              ${variantTitle ? `<p class="t-short uppercase">${variantTitle}</p>` : ""}
            </div>
          </a>
        ` : ""}
      </div>
    `;
    return slide;
  }

  initSwiper() {
    Object.assign(this.carousel, {
      slidesPerView: 1.33,
      mousewheel: { enabled: true, forceToAxis: true },
      pagination: { el: this.querySelector(".reviews__carousel-dots"), clickable: true },
      spaceBetween: 12,
      slidesOffsetAfter: 16,
      slidesOffsetBefore: 16,
      breakpoints: {
        560: { slidesPerView: 2.2 },
        860: { slidesPerView: 3.2 },
        1024: {
          spaceBetween: 24,
          slidesPerView: 4,
          slidesOffsetAfter: 0,
          slidesOffsetBefore: 0
        }
      }
    });
    this.carousel.initialize();
  }
}

if (!customElements.get("reviews-carousel-live")) {
  customElements.define("reviews-carousel-live", ReviewsCarouselLive);
}
